import * as React from 'react';
import { Loading, TOTPSetup, VerifyContact } from 'aws-amplify-react';

import { LabSmartsConfirmSignIn } from './labsmarts-confirm-sign-in';
import { LabSmartsConfirmSignUp } from './labsmarts-confirm-sign-up';
import { LabSmartsForgotPassword } from './labsmarts-forgot-password';
import { LabSmartsRequireNewPassword } from './labsmarts-require-new-password';
import { LabSmartsSignIn } from './labsmarts-sign-in';
import { LabSmartsSignUp } from './labsmarts-sign-up';

/**
 * Amplify feeds this object straight into each element's inline `style`, which
 * is the only way to reach its layout without depending on build-hashed CSS
 * module class names.
 *
 * The spacing is trimmed from Amplify's defaults because Create Account — the
 * tallest screen — scrolled on a laptop, pushing the Phone Number field below
 * the fold. Only the gaps change; the fields themselves keep their padding, so
 * nothing looks cramped. Vertical values are set as longhands so Amplify's own
 * `max-width: 599px` media query keeps supplying the narrower side padding.
 */
export const authTheme = {
  button: { backgroundColor: '#337ab7' },
  formSection: { paddingTop: '24px', paddingBottom: '24px' },
  sectionHeader: { marginBottom: '14px' },
  sectionBody: { marginBottom: '18px' },
  formField: { marginBottom: '14px' },
  // 16px of vertical padding gives Amplify a 55px-tall field; 8px gives 39px.
  // Trimmed from 12px on 2026-10-28 at the user's request, once the two
  // sign-up questions made Create Account taller. The dial-code <select> is
  // styled in CSS rather than through the theme, so App.css mirrors this by
  // hand.
  input: { marginBottom: '4px', padding: '8px 16px' },
  // Amplify pins its error bar to the top of the PAGE (position: absolute), so
  // on Create Account, which scrolls, a missing-field error appeared above the
  // fold and the practitioner saw nothing happen. Fixed pins it to the top of
  // the window instead, wherever the page is scrolled.
  toast: { position: 'fixed' },
};

/**
 * Every field is `required: true`, which is what makes Amplify append " *" to
 * each label (PhoneField draws its own from the same flag). The asterisks went
 * back on 2026-10-28 at the user's request: practitioners skim past the
 * "All fields are required." line, and an asterisk on every label is the cue
 * they actually see, rather than learning it from an error on submit.
 * LabSmartsSignUp overrides `validate()`, so the submit check is ours either
 * way (it also rejects whitespace-only answers).
 */
export const signUpConfig = {
  hideAllDefaults: true,
  signUpFields: [
    {
      label: 'Email',
      key: 'username',
      required: true,
      placeholder: 'e.g. example@example.com',
      type: 'email',
      displayOrder: 1,
    },
    {
      label: 'Password',
      key: 'password',
      required: true,
      placeholder: 'Password',
      type: 'password',
      displayOrder: 2,
    },
    {
      label: 'First Name',
      key: 'given_name',
      required: true,
      placeholder: 'e.g. MaryAnn',
      displayOrder: 3,
    },
    {
      label: 'Last Name',
      key: 'family_name',
      required: true,
      placeholder: 'e.g. Marks',
      displayOrder: 4,
    },
    {
      label: 'Phone Number',
      key: 'phone_number',
      required: true,
      placeholder: 'e.g. 3115551212',
      displayOrder: 5,
    },
    // Free text on purpose, so nobody is forced into a list that doesn't fit.
    // The `custom:` keys are Cognito custom attributes; both pools define them
    // (prod caps practitioner_type at 256 characters, referral_source at 128).
    // postSignUp copies them onto the UserProfile as practitionerType and
    // referralSource, and prod forwards them to ActiveCampaign.
    {
      label: 'Practitioner Type',
      key: 'custom:practitioner_type',
      required: true,
      placeholder: 'e.g. ND, Nutrition Consultant',
      displayOrder: 6,
    },
    {
      label: 'How Did You Hear About Us?',
      key: 'custom:referral_source',
      required: true,
      placeholder: 'e.g. a colleague, a webinar, a podcast',
      displayOrder: 7,
    },
  ],
};

/**
 * Every screen the Authenticator can show.
 *
 * Supplying any custom component turns on the Authenticator's `hideDefault`,
 * so this list has to be complete — a screen left out here renders nothing at
 * all. Greetings is the deliberate exception: the sign-out header is off
 * (see `displaySignoutHeader` in SecureApp.tsx).
 *
 * authenticator-screens.test.tsx renders this exact array to guard that.
 */
export const authenticatorComponents = [
  <LabSmartsSignIn key="signIn" />,
  <LabSmartsConfirmSignIn key="confirmSignIn" />,
  <LabSmartsRequireNewPassword key="requireNewPassword" />,
  <LabSmartsSignUp key="signUp" signUpConfig={signUpConfig} />,
  <LabSmartsConfirmSignUp key="confirmSignUp" />,
  <VerifyContact key="verifyContact" />,
  <LabSmartsForgotPassword key="forgotPassword" />,
  <TOTPSetup key="totpSetup" />,
  <Loading key="loading" />,
];
